import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { brl } from "./PurchaseDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { UserCheck } from "lucide-react";

export const TransferForm = ({ w, onDone }) => {
  const [ident, setIdent] = useState("");
  const [who, setWho] = useState(null);
  const [value, setValue] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const amount = Number(value.replace(",", ".")) || 0;
  const total = amount + w.fee;
  const left = Math.max(0, w.daily_limit - w.sent_today);
  const error = !amount ? "" : amount < w.min ? `Valor mínimo: ${brl(w.min)}`
    : amount > left ? `Limite diário: disponível hoje ${brl(left)}` : total > w.balance ? "Saldo insuficiente (valor + taxa)" : "";

  const lookup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/transfers/lookup", { recipient: ident }); setWho(data); }
    catch (e) { setWho(null); toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/transfers", { recipient: ident, amount, password });
      toast.success(`${brl(amount)} transferido para ${who.name}`);
      setIdent(""); setWho(null); setValue(""); setPassword("");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 text-sm" data-testid="transfer-form">
      <div className="space-y-1.5">
        <Label htmlFor="tr-to">Destinatário</Label>
        <div className="flex gap-2">
          <Input id="tr-to" placeholder="Código de indicação, e-mail ou CPF" value={ident}
                 onChange={(e) => { setIdent(e.target.value); setWho(null); }} data-testid="transfer-recipient-input" />
          <Button type="button" variant="outline" disabled={busy || ident.trim().length < 2} onClick={lookup} data-testid="transfer-lookup-btn">Buscar</Button>
        </div>
        {who && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="transfer-recipient-found">
            <UserCheck className="h-4 w-4 text-emerald-500" />
            <span><b data-testid="transfer-recipient-name">{who.name}</b> <span className="text-xs text-muted-foreground">· {who.referral_code} · CPF {who.cpf_masked}</span></span>
          </div>
        )}
      </div>
      {who && (<>
        <div className="space-y-1.5">
          <Label htmlFor="tr-amount">Valor</Label>
          <Input id="tr-amount" inputMode="decimal" placeholder={`Mínimo ${brl(w.min)}`} value={value}
                 onChange={(e) => setValue(e.target.value)} data-testid="transfer-amount-input" />
        </div>
        <div className="rounded-xl bg-muted/50 p-4 space-y-2">
          <div className="flex justify-between"><span>{who.name.split(" ")[0]} recebe</span><span className="font-mono" data-testid="transfer-receives">{brl(amount)}</span></div>
          <div className="flex justify-between"><span>Taxa de transferência</span><span className="font-mono text-rose-500" data-testid="transfer-fee">+ {brl(w.fee)}</span></div>
          <div className="flex justify-between border-t border-border pt-2"><span>Total descontado de você</span>
            <b className="font-mono gold-text" data-testid="transfer-total">{brl(amount ? total : 0)}</b></div>
        </div>
        {error && <p className="text-xs text-rose-500" data-testid="transfer-error">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="tr-pwd">Senha de acesso</Label>
          <Input id="tr-pwd" type="password" autoComplete="current-password" placeholder="Digite sua senha para confirmar"
                 value={password} onChange={(e) => setPassword(e.target.value)} data-testid="transfer-password-input" />
        </div>
        <Button type="submit" className="gold-btn w-full" disabled={busy || !amount || !!error || !password} data-testid="transfer-confirm-btn">
          {busy ? "Enviando..." : `Transferir para ${who.name.split(" ")[0]}`}
        </Button>
      </>)}
    </form>
  );
};
